import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { useAuth } from '../contexts/AuthContext';
import { 
  Settings as SettingsIcon, User, Mail, CheckCircle, XCircle, 
  Loader2, AlertTriangle, Send, Save, Briefcase
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  
  // Profile state
  const [profileData, setProfileData] = useState({
    name: '',
    job_title: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Gmail state
  const [gmailStatus, setGmailStatus] = useState(null);
  const [gmailLoading, setGmailLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState(null);

  // Initialize profile data from user
  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        job_title: user.job_title || ''
      });
    }
  }, [user]);

  const fetchGmailStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/gmail/status`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setGmailStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch Gmail status:', error);
    } finally {
      setGmailLoading(false);
    }
  };

  useEffect(() => {
    fetchGmailStatus();
  }, []);

  const saveProfile = async () => {
    setSavingProfile(true);
    setMessage(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/${user.user_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: profileData.name,
          job_title: profileData.job_title
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Profile updated successfully' });
        if (refreshUser) refreshUser();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to update profile' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setSavingProfile(false);
    }
  };

  const testGmailConnection = async () => {
    setTesting(true);
    setMessage(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/gmail/test`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: data.message });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Gmail test failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to test Gmail connection' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <AppLayout>
      <div className="p-6 max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <SettingsIcon className="w-8 h-8 text-[#A2FF00]" />
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Settings
            </h1>
          </div>
          <p className="text-gray-400">
            Manage your profile and email integration settings.
          </p>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-sm border flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-green-500/10 border-green-500/30' 
              : 'bg-red-500/10 border-red-500/30'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-400" />
            ) : (
              <XCircle className="w-5 h-5 text-red-400" />
            )}
            <span className={message.type === 'success' ? 'text-green-400' : 'text-red-400'}>
              {message.text}
            </span>
          </div>
        )}

        {/* Profile Section */}
        <div className="bg-[#111] border border-white/10 rounded-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-[#A2FF00]" />
              <h2 className="text-lg font-semibold text-white">Profile</h2>
            </div>
            <button
              onClick={saveProfile}
              disabled={savingProfile}
              className="flex items-center gap-2 px-4 py-2 bg-[#A2FF00] text-black font-bold rounded-sm hover:bg-[#8FE600] transition-colors disabled:opacity-50"
            >
              {savingProfile ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Name</label>
              <input
                type="text"
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-white/20 rounded-sm px-3 py-2 text-white focus:border-[#A2FF00] outline-none"
                placeholder="Your full name"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                <span className="flex items-center gap-2">
                  <Briefcase className="w-3 h-3" />
                  Job Title
                </span>
              </label>
              <input
                type="text"
                value={profileData.job_title}
                onChange={(e) => setProfileData({ ...profileData, job_title: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-white/20 rounded-sm px-3 py-2 text-white focus:border-[#A2FF00] outline-none"
                placeholder="e.g., Sales Director, Account Manager"
              />
              <p className="text-xs text-gray-500 mt-1">
                This will be available as {"{{senders_title}}"} in email templates
              </p>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Email</label>
              <div className="text-white bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2">
                {user?.email || 'Not set'}
              </div>
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed (managed by Google login)</p>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Role</label>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#A2FF00]/10 text-[#A2FF00] rounded-sm text-sm">
                {user?.role === 'admin' ? 'Administrator' : 'Member'}
              </div>
            </div>
          </div>
        </div>

        {/* Gmail Integration Section */}
        <div className="bg-[#111] border border-white/10 rounded-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <Mail className="w-5 h-5 text-[#A2FF00]" />
            <h2 className="text-lg font-semibold text-white">Email Integration</h2>
          </div>
          
          <p className="text-gray-400 text-sm mb-6">
            Send proposals and communications directly from your email address.
          </p>

          {gmailLoading ? (
            <div className="flex items-center gap-3 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Loading Gmail status...</span>
            </div>
          ) : gmailStatus?.connected ? (
            // Connected State - Service Account Mode
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-sm">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <div className="flex-1">
                  <div className="text-green-400 font-medium">Gmail Enabled</div>
                  <div className="text-gray-400 text-sm">{gmailStatus.email}</div>
                </div>
              </div>
              
              <div className="text-sm text-gray-400 space-y-1">
                <p>✓ Proposals will be sent from your email address</p>
                <p>✓ Recipients will see emails from {gmailStatus.email}</p>
                <p>✓ No additional setup required</p>
              </div>
              
              <button
                onClick={testGmailConnection}
                disabled={testing}
                className="flex items-center gap-2 px-4 py-2 bg-[#A2FF00]/20 hover:bg-[#A2FF00]/30 text-[#A2FF00] rounded-sm transition-colors disabled:opacity-50"
              >
                {testing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Test Gmail Connection
              </button>
            </div>
          ) : (
            // Not Connected State
            <div className="space-y-4">
              {gmailStatus?.mode === "not_configured" ? (
                <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-yellow-400 font-medium">Setup Required</div>
                    <div className="text-gray-400 text-sm mt-1">
                      Gmail integration requires configuration by an administrator.
                    </div>
                  </div>
                </div>
              ) : gmailStatus?.mode === "disabled" ? (
                <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-yellow-400 font-medium">Temporarily Disabled</div>
                    <div className="text-gray-400 text-sm mt-1">
                      Gmail integration has been temporarily disabled by an administrator.
                    </div>
                  </div>
                </div>
              ) : gmailStatus?.mode === "domain_mismatch" ? (
                <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-sm">
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-red-400 font-medium">Email Domain Not Supported</div>
                    <div className="text-gray-400 text-sm mt-1">
                      {gmailStatus.message}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-4 bg-gray-800/50 border border-white/10 rounded-sm">
                  <XCircle className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-gray-300 font-medium">Gmail Not Available</div>
                    <div className="text-gray-500 text-sm">{gmailStatus?.message || 'Contact administrator for setup'}</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default Settings;
